import { Linking } from "react-native";

/**
 * Quality metadata about how a fix was obtained. Optional everywhere: builds
 * before 2026-09 sent none, and every reader must cope with its absence.
 */
export interface CaptureQuality {
    /** Epoch ms of the OS fix we kept (NOT when we asked). */
    capturedAt: number;
    /** How many fixes the OS delivered during the sampling window. */
    sampleCount: number;
    /** How many of those we discarded for predating the window (cached fixes). */
    staleCount: number;
    /** Wall-clock ms actually spent sampling. */
    settledMs: number;
    /** Android only: the OS says a mock-location app produced this fix. */
    mocked?: boolean;
}

export type ClockLocation = {
    lat: number;
    lng: number;
    accuracy?: number;
    quality?: CaptureQuality;
};

/**
 * Why a clock GPS capture failed — drives the "no GPS" step's guidance + buttons.
 * `denied_can_ask` / `no_fix` → a plain "Retry location" can still help (the retry
 * re-shows the OS prompt when undetermined, or just re-reads the GPS). `services_off`
 * / `denied_permanent` → retrying is pointless until the nurse changes an OS setting,
 * so we surface an "Open phone settings" deep-link instead. See openLocationSettings.
 */
export type LocationBlockReason =
    | "services_off" // device-wide Location Services are off
    | "denied_permanent" // app permission denied; the OS will NOT re-prompt → Settings only
    | "denied_can_ask" // not granted yet, but the OS will still show the prompt
    | "no_fix"; // permission granted but no GPS fix (indoors / weak signal)

export type CaptureResult =
    { location: ClockLocation } | { location: null; reason: LocationBlockReason };

/** One fix as delivered by the OS, flattened so the picker stays pure. */
export interface LocationSample {
    lat: number;
    lng: number;
    /** Reported uncertainty radius in METERS, or null when the OS gives none. */
    accuracy: number | null;
    /** Epoch ms the OS says the fix was taken. */
    timestamp: number;
    mocked?: boolean;
}

/**
 * How long a clock-in / clock-out capture may spend improving its fix, and the
 * accuracy at which it stops early and takes what it has.
 *
 * 8s is the ceiling, not the cost: a nurse outdoors settles on the first or
 * second fix (well under a second). The ceiling only bites indoors, which is
 * exactly the case that used to produce a garbage coordinate.
 */
export const CLOCK_SETTLE_MS = 8_000;
export const CLOCK_TARGET_ACCURACY_M = 20;

/** Mid-visit pings are cheaper: the trail tolerates a looser fix than a clock event. */
export const PING_SETTLE_MS = 3_000;
export const PING_TARGET_ACCURACY_M = 35;

/**
 * A fix may legitimately carry a timestamp slightly older than the moment we
 * asked (the OS hands over the reading it just took). Beyond this it is a
 * CACHED fix, and cached fixes are the main source of the "clocked in a mile
 * away" reports: the device answers instantly from its last known position,
 * often from the nurse's previous visit or her drive over, and reports a small
 * uncertainty radius alongside it so the accuracy-aware distance check trusts
 * it. See packages/shared/src/geo.ts.
 */
export const STALE_FIX_TOLERANCE_MS = 30_000;

/** Lower accuracy value = tighter fix. Missing accuracy sorts worst. */
function accuracyRank(sample: LocationSample): number {
    return sample.accuracy != null && sample.accuracy > 0
        ? sample.accuracy
        : Number.POSITIVE_INFINITY;
}

export interface PickedSample {
    sample: LocationSample | null;
    /** True when the chosen sample was taken inside the capture window. */
    fresh: boolean;
    sampleCount: number;
    staleCount: number;
}

/**
 * Choose the fix to keep out of everything the OS delivered.
 *
 * Prefers the tightest FRESH fix. Falls back to the newest stale one rather
 * than returning nothing: a stale fix still beats blocking the nurse on the
 * "no GPS" step, and the returned `fresh` flag lets the caller record that the
 * point is weaker evidence.
 *
 * Pure so it can be tested without an OS location provider.
 */
export function pickBestSample(samples: LocationSample[], startedAt: number): PickedSample {
    const cutoff = startedAt - STALE_FIX_TOLERANCE_MS;
    const fresh: LocationSample[] = [];
    const stale: LocationSample[] = [];
    for (const sample of samples) {
        (sample.timestamp >= cutoff ? fresh : stale).push(sample);
    }

    const best = (pool: LocationSample[]): LocationSample | null =>
        pool.reduce<LocationSample | null>((winner, candidate) => {
            if (winner == null) {
                return candidate;
            }
            const byAccuracy = accuracyRank(candidate) - accuracyRank(winner);
            if (byAccuracy !== 0) {
                return byAccuracy < 0 ? candidate : winner;
            }
            // Equal (or equally unknown) accuracy: the newer reading wins.
            return candidate.timestamp > winner.timestamp ? candidate : winner;
        }, null);

    const freshBest = best(fresh);
    if (freshBest != null) {
        return {
            sample: freshBest,
            fresh: true,
            sampleCount: samples.length,
            staleCount: stale.length
        };
    }
    // Nothing fresh arrived. Newest stale reading, or nothing at all.
    const staleBest = stale.reduce<LocationSample | null>(
        (winner, candidate) =>
            winner == null || candidate.timestamp > winner.timestamp ? candidate : winner,
        null
    );
    return {
        sample: staleBest,
        fresh: false,
        sampleCount: samples.length,
        staleCount: stale.length
    };
}

export interface CaptureOptions {
    /** Ceiling on the sampling window. Defaults to {@link CLOCK_SETTLE_MS}. */
    settleMs?: number;
    /** Stop as soon as a fresh fix is this accurate (meters). */
    targetAccuracyM?: number;
    /** Called as fixes arrive, so the UI can show the fix tightening. */
    onProgress?: (accuracyM: number | null) => void;
}

/**
 * Capture a GPS fix for clock-in / clock-out / a mid-visit ping.
 *
 * Unlike a plain permission request, this distinguishes WHY there's no location
 * so the modal can react correctly:
 *   - If the OS can still be asked (first-ever ask, or Android before
 *     "don't ask again"), it shows the system prompt here.
 *   - After a hard denial iOS/Android silently no-op the request — `canAskAgain`
 *     tells us, and the modal then points the nurse at OS Settings (the only way
 *     to re-enable a denied permission) rather than a Retry that can't work.
 *
 * A missing location never proceeds silently: the clock-in/out modals turn a
 * `{ location: null }` into a blocking step (retry / open settings / type a
 * reason), and the API enforces the same (visit.service.ts
 * `assertLocationOrReason`). This stops the coordinate-less clock-ins that pile
 * up when a phone's location goes dark.
 *
 * ─── Why this samples instead of asking once ───
 *
 * It used to be a single `getCurrentPositionAsync`, first answer wins. The OS
 * is free to satisfy that instantly from cache or from wifi/cell positioning,
 * and both can be confidently wrong by a mile while reporting a tight accuracy
 * radius, which then survives the accuracy-aware "too far" check and red-flags
 * a nurse who is standing in the patient's living room.
 *
 * So: watch for up to `settleMs`, drop readings that predate the window, keep
 * the tightest one, and stop early the moment a fix is good enough. If the
 * watch yields nothing at all we fall back to the old single call, so this can
 * never turn a clock-in that used to work into a blocked one.
 */
export async function captureLocation(options: CaptureOptions = {}): Promise<CaptureResult> {
    const settleMs = options.settleMs ?? CLOCK_SETTLE_MS;
    const targetAccuracyM = options.targetAccuracyM ?? CLOCK_TARGET_ACCURACY_M;

    try {
        const Location = await import("expo-location");

        // Device-wide Location Services (distinct from the app's own permission).
        const servicesOn = await Location.hasServicesEnabledAsync().catch(() => true);

        // Read the current permission WITHOUT prompting, then prompt only if the
        // OS still allows it. After a permanent denial the request is a silent
        // no-op, so we must not rely on it to "wake up" the dialog.
        let perm = await Location.getForegroundPermissionsAsync();
        if (perm.status !== "granted" && perm.canAskAgain) {
            perm = await Location.requestForegroundPermissionsAsync();
        }
        if (perm.status !== "granted") {
            return {
                location: null,
                reason: perm.canAskAgain ? "denied_can_ask" : "denied_permanent"
            };
        }
        if (!servicesOn) {
            return { location: null, reason: "services_off" };
        }

        const startedAt = Date.now();
        const samples: LocationSample[] = [];

        await new Promise<void>((resolve) => {
            let settled = false;
            let subscription: { remove: () => void } | null = null;

            const finish = () => {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                subscription?.remove();
                resolve();
            };

            const timer = setTimeout(finish, settleMs);

            Location.watchPositionAsync(
                {
                    accuracy: Location.Accuracy.Highest,
                    // Report every reading: we want the sequence, not a filtered
                    // "you moved far enough" stream.
                    distanceInterval: 0,
                    timeInterval: 1000
                },
                (position) => {
                    const sample: LocationSample = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                        accuracy: position.coords.accuracy ?? null,
                        timestamp: position.timestamp,
                        mocked: position.mocked
                    };
                    samples.push(sample);
                    options.onProgress?.(sample.accuracy);

                    const isFresh = sample.timestamp >= startedAt - STALE_FIX_TOLERANCE_MS;
                    if (
                        isFresh &&
                        sample.accuracy != null &&
                        sample.accuracy > 0 &&
                        sample.accuracy <= targetAccuracyM
                    ) {
                        finish();
                    }
                }
            ).then(
                (sub) => {
                    subscription = sub;
                    // The window may already have closed while the watch was
                    // starting up; don't leak the subscription if so.
                    if (settled) {
                        sub.remove();
                    }
                },
                () => finish()
            );
        });

        const picked = pickBestSample(samples, startedAt);
        const chosen =
            picked.sample ??
            // The watch produced nothing at all. Fall back to the pre-2026-09
            // behaviour so this path is never worse than what it replaced.
            (await (async (): Promise<LocationSample | null> => {
                const position = await Location.getCurrentPositionAsync({
                    accuracy: Location.Accuracy.High
                });
                return {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy ?? null,
                    timestamp: position.timestamp,
                    mocked: position.mocked
                };
            })());

        if (chosen == null) {
            return { location: null, reason: "no_fix" };
        }

        return {
            location: {
                lat: chosen.lat,
                lng: chosen.lng,
                accuracy: chosen.accuracy ?? undefined,
                quality: {
                    capturedAt: chosen.timestamp,
                    sampleCount: picked.sampleCount,
                    staleCount: picked.staleCount,
                    settledMs: Date.now() - startedAt,
                    ...(chosen.mocked === true ? { mocked: true } : {})
                }
            }
        };
    } catch {
        // Permission was granted but the fix itself failed (indoors / weak signal).
        return { location: null, reason: "no_fix" };
    }
}

/** Deep-link to this app's OS settings page (iOS + Android). */
export function openLocationSettings(): void {
    void Linking.openSettings();
}

/** State-aware copy + whether to offer the "Open settings" deep-link. */
export function locationBlockCopy(
    reason: LocationBlockReason,
    verb: "in" | "out"
): { title: string; message: string; showSettings: boolean } {
    const clock = verb === "in" ? "clock in" : "clock out";
    const startsEnds = verb === "in" ? "starts" : "ends";
    switch (reason) {
        case "services_off":
            return {
                title: "Turn on Location Services",
                message:
                    `Your phone's Location Services are off, so no app can find you. Turn ` +
                    `them on, then retry — or, if you can't, tell us why and you can ${clock} ` +
                    `without it.`,
                showSettings: true
            };
        case "denied_permanent":
            return {
                title: "Turn on location for TaskHealth",
                message:
                    `Location is turned off for the TaskHealth app, and your phone won't ask ` +
                    `again from here. Open Settings and allow Location ("While Using"), then ` +
                    `come back and retry — or tell us why and ${clock} without it.`,
                showSettings: true
            };
        case "denied_can_ask":
        case "no_fix":
        default:
            return {
                title: `Turn on location to ${clock}`,
                message:
                    `We couldn't get your GPS location. Task Health records where each visit ` +
                    `${startsEnds}, so please allow Location for the app and try again. If you ` +
                    `truly have no signal, tell us why and you can ${clock} without it.`,
                showSettings: false
            };
    }
}
